import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { localize as localizeImpl, LocalizeOptions } from '../helpers/localize';
import { getBrandLanguage } from '../helpers/brandHelpers';
import {
  selectDeviceLanguage,
  selectDeviceUUID,
  selectTexts,
  selectUser,
  selectXBrandId,
} from '../selectors';

type Localize = (
  key: string,
  ...params: Array<string | number | LocalizeOptions>
) => string;

export type LocalizePropType = (
  key: string,
  ...params: Array<string | number | LocalizeOptions>
) => string;

export type Props = {
  localize: LocalizePropType;
};

export default function withTextLocalizer<P extends { localize: Localize }>(
  Wrapped: React.ComponentType<P>
) {
  return function TextLocalizerWrapper(props: Omit<P, 'localize'>) {
    const savedLang = useSelector(selectDeviceLanguage);
    const user = useSelector(selectUser);
    const xBrandId = useSelector(selectXBrandId);
    const deviceUUID = useSelector(selectDeviceUUID);
    const texts = useSelector(selectTexts);

    const dictionary = useMemo(
      () => ({
        ...texts,
        language: savedLang || getBrandLanguage(xBrandId),
        group: 'default',
      }),
      [texts, savedLang, xBrandId]
    );

    const uniqueId = user?.id || deviceUUID;

    const localize = (key, ...params: any) =>
      localizeImpl(dictionary as any, uniqueId, key, ...params);
    // NOTE: we don’t pass dictionary/dispatch, so your old mapProps
    // (which removed them) is no longer needed.
    return <Wrapped {...(props as P)} localize={localize} />;
  };
}
